/**
 * Spot Snapping Utilities for RampEditorV2
 * Provides edge-to-edge snapping when dragging Spots while holding Shift
 */

import { Point, Shape } from "./RampCanvasV2";
import { getSpotGeometry } from "./spotHelpers";

/**
 * Guide line to draw during snapping
 */
export type GuideLine = {
  type: "vertical" | "horizontal";
  position: number; // x for vertical, y for horizontal
  start: number; // y for vertical, x for horizontal
  end: number;
};

/**
 * Result of snap calculation
 */
export type SnapResult = {
  snappedX: number;
  snappedY: number;
  snappedAngle: number | null; // radians, null if no rotation snap
  guideLines: GuideLine[];
  didSnap: boolean;
};

/**
 * Bounding box edges for a spot
 */
type SpotEdges = {
  left: number;
  right: number;
  top: number;
  bottom: number;
  angle: number; // radians
  center: Point;
};

/**
 * Get the axis-aligned bounding box edges for a spot
 */
function getSpotEdges(spot: Shape): SpotEdges {
  const geo = getSpotGeometry(spot);
  return {
    left: geo.minX,
    right: geo.maxX,
    top: geo.minY,
    bottom: geo.maxY,
    angle: geo.angle,
    center: geo.center,
  };
}

/**
 * Get edges for a spot at a hypothetical position (offset from current)
 */
function getEdgesAtOffset(spot: Shape, dx: number, dy: number): SpotEdges {
  const geo = getSpotGeometry(spot);
  return {
    left: geo.minX + dx,
    right: geo.maxX + dx,
    top: geo.minY + dy,
    bottom: geo.maxY + dy,
    angle: geo.angle,
    center: { x: geo.center.x + dx, y: geo.center.y + dy },
  };
}

/**
 * Check if two angles are within threshold (in radians)
 * Threshold of ~0.09 radians = ~5 degrees
 */
function anglesAreClose(angle1: number, angle2: number, threshold: number = 0.09): boolean {
  // Normalize angles to [0, 2*PI)
  const normalize = (a: number) => {
    while (a < 0) a += Math.PI * 2;
    while (a >= Math.PI * 2) a -= Math.PI * 2;
    return a;
  };

  const a1 = normalize(angle1);
  const a2 = normalize(angle2);

  // Check direct difference and wrap-around
  const diff = Math.abs(a1 - a2);
  return diff < threshold || Math.abs(diff - Math.PI * 2) < threshold;
}

const SEARCH_RADIUS = 400; // Only check spots within this radius (pixels)

/**
 * Calculate snap positions for a dragged spot
 *
 * @param draggedSpot - The spot being dragged
 * @param allShapes - All shapes in the ramp (will filter to Spots)
 * @param proposedDx - Proposed X delta from current position
 * @param proposedDy - Proposed Y delta from current position
 * @param threshold - Snap distance threshold in pixels (default 35)
 * @returns SnapResult with adjusted position, rotation, and guide lines
 */
export function calculateSnapPositions(
  draggedSpot: Shape,
  allShapes: Shape[],
  proposedDx: number,
  proposedDy: number,
  threshold: number = 35
): SnapResult {
  // Filter to other Spots only
  const otherSpots = allShapes.filter(
    (s) => s.id !== draggedSpot.id && s.type === "Spot"
  );

  // Get dragged spot's current center for proximity filtering
  const draggedGeo = getSpotGeometry(draggedSpot);
  const proposedCenter = {
    x: draggedGeo.center.x + proposedDx,
    y: draggedGeo.center.y + proposedDy,
  };

  // Filter to nearby spots for performance
  const nearbySpots = otherSpots.filter((s) => {
    const geo = getSpotGeometry(s);
    return (
      Math.abs(geo.center.x - proposedCenter.x) < SEARCH_RADIUS &&
      Math.abs(geo.center.y - proposedCenter.y) < SEARCH_RADIUS
    );
  });

  // Get dragged spot's edges at proposed position
  const draggedEdges = getEdgesAtOffset(draggedSpot, proposedDx, proposedDy);

  let bestSnapDx = proposedDx;
  let bestSnapDy = proposedDy;
  let bestSnapAngle: number | null = null;
  const guideLines: GuideLine[] = [];

  let closestXDist = threshold + 1;
  let closestYDist = threshold + 1;
  let xSnapTarget: SpotEdges | null = null;
  let ySnapTarget: SpotEdges | null = null;

  for (const targetSpot of nearbySpots) {
    const targetEdges = getSpotEdges(targetSpot);

    // Check horizontal alignments (affects X position)
    // left-to-left, left-to-right, right-to-left, right-to-right
    const horizontalChecks = [
      { from: "left" as const, to: "left" as const },
      { from: "left" as const, to: "right" as const },
      { from: "right" as const, to: "left" as const },
      { from: "right" as const, to: "right" as const },
    ];

    for (const check of horizontalChecks) {
      const draggedEdge = check.from === "left" ? draggedEdges.left : draggedEdges.right;
      const targetEdge = check.to === "left" ? targetEdges.left : targetEdges.right;
      const dist = Math.abs(draggedEdge - targetEdge);

      if (dist < closestXDist) {
        closestXDist = dist;
        // Calculate the adjustment needed to align edges
        const adjustment = targetEdge - draggedEdge;
        bestSnapDx = proposedDx + adjustment;
        xSnapTarget = targetEdges;
      }
    }

    // Check vertical alignments (affects Y position)
    // top-to-top, top-to-bottom, bottom-to-top, bottom-to-bottom
    const verticalChecks = [
      { from: "top" as const, to: "top" as const },
      { from: "top" as const, to: "bottom" as const },
      { from: "bottom" as const, to: "top" as const },
      { from: "bottom" as const, to: "bottom" as const },
    ];

    for (const check of verticalChecks) {
      const draggedEdge = check.from === "top" ? draggedEdges.top : draggedEdges.bottom;
      const targetEdge = check.to === "top" ? targetEdges.top : targetEdges.bottom;
      const dist = Math.abs(draggedEdge - targetEdge);

      if (dist < closestYDist) {
        closestYDist = dist;
        // Calculate the adjustment needed to align edges
        const adjustment = targetEdge - draggedEdge;
        bestSnapDy = proposedDy + adjustment;
        ySnapTarget = targetEdges;
      }
    }
  }

  const didSnapX = closestXDist <= threshold;
  const didSnapY = closestYDist <= threshold;
  const didSnap = didSnapX || didSnapY;

  // Generate guide lines for active snaps
  if (didSnapX && xSnapTarget) {
    // Recalculate dragged edges at snapped position
    const snappedDraggedEdges = getEdgesAtOffset(draggedSpot, bestSnapDx, bestSnapDy);

    // Find which edge aligned (check all possibilities)
    let alignedX: number | null = null;
    if (Math.abs(snappedDraggedEdges.left - xSnapTarget.left) < 1) {
      alignedX = xSnapTarget.left;
    } else if (Math.abs(snappedDraggedEdges.left - xSnapTarget.right) < 1) {
      alignedX = xSnapTarget.right;
    } else if (Math.abs(snappedDraggedEdges.right - xSnapTarget.left) < 1) {
      alignedX = xSnapTarget.left;
    } else if (Math.abs(snappedDraggedEdges.right - xSnapTarget.right) < 1) {
      alignedX = xSnapTarget.right;
    }

    if (alignedX !== null) {
      guideLines.push({
        type: "vertical",
        position: alignedX,
        start: Math.min(xSnapTarget.top, snappedDraggedEdges.top) - 40,
        end: Math.max(xSnapTarget.bottom, snappedDraggedEdges.bottom) + 40,
      });
    }

    // Check for rotation snap with X-aligned spot
    if (anglesAreClose(draggedEdges.angle, xSnapTarget.angle)) {
      bestSnapAngle = xSnapTarget.angle;
    }
  }

  if (didSnapY && ySnapTarget) {
    // Recalculate dragged edges at snapped position
    const snappedDraggedEdges = getEdgesAtOffset(draggedSpot, bestSnapDx, bestSnapDy);

    // Find which edge aligned
    let alignedY: number | null = null;
    if (Math.abs(snappedDraggedEdges.top - ySnapTarget.top) < 1) {
      alignedY = ySnapTarget.top;
    } else if (Math.abs(snappedDraggedEdges.top - ySnapTarget.bottom) < 1) {
      alignedY = ySnapTarget.bottom;
    } else if (Math.abs(snappedDraggedEdges.bottom - ySnapTarget.top) < 1) {
      alignedY = ySnapTarget.top;
    } else if (Math.abs(snappedDraggedEdges.bottom - ySnapTarget.bottom) < 1) {
      alignedY = ySnapTarget.bottom;
    }

    if (alignedY !== null) {
      guideLines.push({
        type: "horizontal",
        position: alignedY,
        start: Math.min(ySnapTarget.left, snappedDraggedEdges.left) - 40,
        end: Math.max(ySnapTarget.right, snappedDraggedEdges.right) + 40,
      });
    }

    // Check for rotation snap with Y-aligned spot (if not already set)
    if (bestSnapAngle === null && anglesAreClose(draggedEdges.angle, ySnapTarget.angle)) {
      bestSnapAngle = ySnapTarget.angle;
    }
  }

  return {
    snappedX: bestSnapDx,
    snappedY: bestSnapDy,
    snappedAngle: bestSnapAngle,
    guideLines,
    didSnap,
  };
}

/**
 * Rotate a spot's points to a target angle
 * Rotates around the spot's center
 */
export function rotateSpotToAngle(spot: Shape, targetAngle: number): Point[] {
  const geo = getSpotGeometry(spot);
  const currentAngle = geo.angle;
  const angleDiff = targetAngle - currentAngle;

  if (Math.abs(angleDiff) < 0.001) {
    // No rotation needed
    return spot.points;
  }

  const center = geo.center;
  const cos = Math.cos(angleDiff);
  const sin = Math.sin(angleDiff);

  return spot.points.map((point) => {
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    return {
      x: center.x + dx * cos - dy * sin,
      y: center.y + dx * sin + dy * cos,
    };
  });
}
